import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  CheckIcon,
  CloseIcon,
  IconButton,
  InputUpload,
  Modal,
  TextArea,
  TextInput,
  Typography,
} from "@povio/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { CancelIcon } from "@/assets/icons/general/CancelIcon";
import { Trash2Icon } from "@/assets/icons/general/Trash2";
import EmptyState from "@/assets/images/home/EmptyState.png";
import { UnsavedChangesModal } from "@/components/home/modals/UnsavedChangesModal";
import { MediaModels } from "@/data/media/media.models";
import { useIntroductionCoverImageUpload } from "@/data/media/useIntroductionCoverImageUpload";
import type { ProfileModels } from "@/data/profile/profile.models";
import { ProfileQueries } from "@/data/profile/profile.queries";
import { PublicProfileQueries } from "@/data/publicProfile/publicProfile.queries";
import useInvalidateProfile from "@/hooks/profile/useInvalidateProfile";
import { useToast } from "@/hooks/useToast";
import { useUnsavedChangesModal } from "@/hooks/useUnsavedChangesModal";
import { ProfileUtils } from "@/util/profile.utils";

const videoUrlSchema = z
  .string()
  .trim()
  .refine(
    (url) => {
      if (!url) return true; // Allow empty strings for optional field
      const youtubeRegex = /(?:youtube\.com|youtu\.be)(?:\/(?:watch\?v=|shorts\/|live\/)|\/?)([a-zA-Z0-9_-]{11})/;
      const vimeoRegex = /(?:vimeo\.com)\/([0-9]+)/;
      return youtubeRegex.test(url) || vimeoRegex.test(url);
    },
    { message: "Only YouTube (including Live and Shorts) or Vimeo video URLs are allowed" },
  )
  .optional();

interface IntroductionFormModalProps {
  isOpen: boolean;
  onClose: () => void;

  userProfile?: ProfileModels.FullProfileResponseDto | null;
}

// Define a combined form type
type FormValues = {
  introduction: string;
  videoUrl?: string;
  introductionTitle: string;
  contactEmail: string;
  contactPhone: string;
  contactPhoneCountry?: string | null;
  linkedin?: string;
  instagram?: string;
  youtube?: string;
  facebook?: string;
};

const IntroductionFormModal = ({ isOpen, onClose, userProfile }: IntroductionFormModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { successToast, errorToast } = useToast();
  const queryClient = useQueryClient();
  const { invalidateAndRefetchProfile } = useInvalidateProfile();
  const { mutateAsync: updateIntroduction } = ProfileQueries.useUpdateIntroduction({
    onSuccess: () => {
      successToast({ text: "Introduction updated successfully" });
    },
  });
  const { uploadFile: uploadCoverImage } = useIntroductionCoverImageUpload(MediaModels.Use.IMAGE, {
    onSuccess: (data) => {
      setCoverImagePreview(data.url);
    },
  });
  const { mutateAsync: validateCoverImage } = ProfileQueries.useValidateIntroductionCoverImageUpload({
    onSuccess: (data) => {
      setCoverImagePreview(data.media?.url || null);
    },
    onError: (error) => {
      errorToast({ text: error.message || "Failed to validate cover image" });
    },
  });
  const coverImageInputRef = useRef<HTMLInputElement>(null);
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null);

  const handleCoverImageSelect = (file: File | null) => {
    if (file) {
      // Validate file size (50MB max)
      if (file.size > 1024 * 1024 * 50) {
        errorToast({ text: "Please upload a file smaller than 50MB" });
        return;
      }

      setCoverImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setCoverImagePreview(previewUrl);
    }

    // Reset input so same file can be selected again
    if (coverImageInputRef.current) {
      coverImageInputRef.current.value = "";
    }
  };

  const handleClearCoverImage = () => {
    if (coverImagePreview) {
      URL.revokeObjectURL(coverImagePreview);
    }

    setCoverImageFile(null);
    setCoverImagePreview(null);

    if (coverImageInputRef.current) {
      coverImageInputRef.current.value = "";
    }
  };

  const handleSaveIntroduction = async (data: FormValues) => {
    setIsLoading(true);

    try {
      // Upload cover image first if a new file is selected
      let coverImageId: string | undefined;
      if (coverImageFile) {
        const uploadResult = await uploadCoverImage(coverImageFile);
        coverImageId = (uploadResult as any)?.id;
        if (coverImageId) {
          await validateCoverImage({ data: { id: coverImageId } as ProfileModels.MediaAttachRequestDto });
        }
      }

      const dataToUpdate: ProfileModels.UpdateIntroductionDto = {
        title: data.introductionTitle,
        introduction: data.introduction,
        videoUrl: data.videoUrl,
      };
      if (!coverImageFile && !coverImagePreview) {
        dataToUpdate.introductionCoverImageId = null;
      }

      await updateIntroduction({ data: dataToUpdate });

      queryClient.invalidateQueries({
        queryKey: PublicProfileQueries.keys.getProfilePublic(userProfile?.user.id || ""),
      });

      await invalidateAndRefetchProfile();
      setIsLoading(false);

      // Clean up preview URL
      if (coverImagePreview && coverImagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(coverImagePreview);
      }

      onClose();
    } catch (error) {
      setIsLoading(false);
      throw error;
    }
  };

  const { handleSubmit, control, reset, formState } = useForm<FormValues>({
    resolver: zodResolver(
      z.object({
        introduction: z.string().trim().min(1, "Description is required"),
        videoUrl: videoUrlSchema,
        introductionTitle: z.string().trim().min(1, "Introduction title is required"),
        contactEmail: z.string().trim().min(1, "Contact email is required"),
        contactPhone: z.string().trim().min(1, "Contact phone is required"),
        contactPhoneCountry: z.string().trim().nullish(),
        linkedin: z.string().trim().optional(),
        instagram: z.string().trim().optional(),
        youtube: z.string().trim().optional(),
        facebook: z.string().trim().optional(),
      }),
    ),
    defaultValues: {
      introduction: "",
      videoUrl: "",
      introductionTitle: "",
      contactEmail: "",
      contactPhone: "",
    },
  });

  // Track both form changes AND cover image changes
  const hasDirtyChanges = formState.isDirty || coverImageFile !== null;

  const {
    confirmExit,
    isModalOpen: isUnsavedChangesModalOpen,
    handleDiscard,
    handleContinueEditing,
  } = useUnsavedChangesModal(hasDirtyChanges);

  useEffect(() => {
    const profileSocials = ProfileUtils.getProfileSocials(userProfile);

    // Extract contact info
    const contactEmail = userProfile?.dataV02?.person?.profile?.contact?.email || "";
    const contactPhone = userProfile?.dataV02?.person?.profile?.contact?.phone || "";
    const contactPhoneCountry = userProfile?.dataV02?.person?.profile?.contact?.phoneCountry || null;

    reset(
      {
        introduction: userProfile?.introductionBio || "",
        videoUrl: userProfile?.introductionVideoUrl || "",
        introductionTitle: userProfile?.introductionTitle || "",
        contactEmail,
        contactPhone: contactPhone || "",
        contactPhoneCountry,
        linkedin: profileSocials.linkedin || "",
        instagram: profileSocials.instagram || "",
        youtube: profileSocials.youtube || "",
        facebook: profileSocials.facebook || "",
      } as FormValues,
      { keepDirty: false, keepErrors: false },
    );

    if (userProfile?.introductionCoverImageUrl) {
      setCoverImagePreview(userProfile.introductionCoverImageUrl);
      setCoverImageFile(null);
    } else if (!coverImageFile) {
      setCoverImagePreview(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile, reset]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (coverImagePreview && coverImagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(coverImagePreview);
      }
    };
  }, [coverImagePreview]);

  const onSubmit = (data: FormValues) => {
    // Process social media handles to full URLs
    const processedSocials = ProfileUtils.processSocialMediaData({
      linkedin: data.linkedin || "",
      instagram: data.instagram || "",
      youtube: data.youtube || "",
      facebook: data.facebook || "",
    });

    handleSaveIntroduction({
      ...data,
      linkedin: processedSocials.linkedin || undefined,
      instagram: processedSocials.instagram || undefined,
      youtube: processedSocials.youtube || undefined,
      facebook: processedSocials.facebook || undefined,
    });
  };

  const getImageUrl = () => {
    if (coverImageFile) {
      return URL.createObjectURL(coverImageFile);
    }

    return coverImagePreview || EmptyState.src;
  };

  const handleCloseModal = () => {
    confirmExit(() => {
      handleClearCoverImage();
      onClose();
    });
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleCloseModal}
        data-testid="edit-introduction-modal"
      >
        <div className="flex w-full items-center justify-between">
          <Typography
            variant="prominent-1"
            size="title-5"
          >
            Introduction
          </Typography>
          <IconButton
            label="Close"
            variant="ghost"
            color="secondary"
            size="none"
            icon={CloseIcon}
            onPress={handleCloseModal}
          />
        </div>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex w-full flex-col gap-6"
        >
          <div
            className="flex aspect-video w-full items-center justify-center rounded-md border border-elevation-outline-default-1 bg-center bg-contain bg-elevation-fill-default-1 bg-no-repeat"
            style={{ backgroundImage: `url(${getImageUrl()})` }}
          >
            {(coverImageFile || coverImagePreview) && (
              <IconButton
                label="Remove image"
                color="secondary"
                size="s"
                icon={Trash2Icon}
                onPress={handleClearCoverImage}
              />
            )}
          </div>

          <InputUpload
            onChange={handleCoverImageSelect}
            value={coverImageFile}
            label="Cover Image (.jpg, .png)"
            acceptedFileTypes={["image/jpeg", "image/jpg", "image/png"]}
            allowsMultiple={false}
            placeholder="Drag & drop new image file here or browse"
            variant="nested"
            isDisabled={isLoading}
            onInvalidFileType={() => {
              errorToast({ text: "Invalid file type. Please upload an image in .jpg or .png format!" });
            }}
          />

          <TextInput
            label="Introduction video link"
            placeholder="https://youtube/lossdogintro"
            data-testid="introduction-video-input"
            formControl={{ control, name: "videoUrl" }}
          />

          {/* Introduction Title */}
          <div className="flex flex-row dm:flex-wrap t:flex-nowrap gap-3">
            <TextInput
              label="Introduction Title"
              placeholder="Name Surname"
              formControl={{ control, name: "introductionTitle" }}
              data-testid="introduction-title-input"
            />
          </div>

          {/* Description */}
          <div>
            <TextArea
              label="Bio"
              placeholder="(Auto generated Bio)"
              data-testid="introduction-description-input"
              formControl={{ control, name: "introduction" }}
              inputClassName="min-h-24"
              className="flex flex-col"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-2">
            <Button
              color="secondary"
              size="s"
              width="fill"
              icon={CheckIcon}
              type="submit"
              isDisabled={isLoading}
              data-testid="introduction-save-button"
              isLoading={isLoading}
            >
              Save
            </Button>
            <Button
              variant="outlined"
              size="s"
              color="secondary"
              icon={CancelIcon}
              width="fill"
              onPress={handleCloseModal}
              isDisabled={isLoading}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>
      <UnsavedChangesModal
        isOpen={isUnsavedChangesModalOpen}
        onClose={handleDiscard}
        onSave={handleContinueEditing}
      />
    </>
  );
};

export default IntroductionFormModal;
